import { Ban, Smartphone, Stethoscope } from "@tamagui/lucide-icons";
import { ScrollView, View, XStack, YStack } from "tamagui";

import DigitalHealth from "@/assets/icons/DigitalHealth";
import Pills from "@/assets/icons/Pills";
import Doctor from "@/assets/images/doctor.png";
import { Button, Footer, Header, Heading, Text, useTheme } from "@/components";
import Image from "@/components/Image";
import { medicalGroupName, shortBrandName } from "@/config/brand";

interface FirstVisitPromoProps {
  onClose?: () => void;
  onContinue?: () => void;
}

export default function FirstVisitPromo({
  onClose,
  onContinue,
}: FirstVisitPromoProps) {
  const iconColor = useTheme().textSecondary?.get();

  const handleClaimVisit = () => {
    onContinue?.();
  };

  const stickyFooter = (
    <Footer padding="$200" mode="dialog">
      <Button
        mode="primary"
        constrained
        onPress={handleClaimVisit}
        dataTrackingId="first-visit-promo-claim-visit"
        flex={1}
      >
        Claim my free visit
      </Button>
    </Footer>
  );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingHorizontal: "$200",
          paddingBottom: "$200",
        }}
      >
        <Header marginBottom="$200">
          <Heading size="$xl">
            For a limited time, Curai Health Medical Group is offering free
            telehealth visits
          </Heading>
        </Header>

        <View
          backgroundColor="$fillClinician"
          borderTopLeftRadius="$150"
          borderTopRightRadius="$150"
          borderWidth="$strokeWidth"
          borderColor="$shadowColor"
          borderBottomWidth={0}
          overflow="hidden"
          padding="$300"
          position="relative"
        >
          <XStack
            position="absolute"
            top="$200"
            left="$200"
            backgroundColor="$background"
            paddingHorizontal="$150"
            paddingVertical="$50"
            borderRadius="$100"
            zIndex={2}
          >
            <Text size="$xs" color="$textPrimary" fontWeight="600">
              LIMITED TIME OFFER
            </Text>
          </XStack>

          <XStack
            justifyContent="space-between"
            alignItems="flex-start"
            marginTop="$400"
            gap="$200"
          >
            <YStack gap="$100" flex={1}>
              <YStack gap="$50">
                <Heading size="$lg">FREE visit</Heading>
                <Text size="$sm" bold>
                  ($39 value)
                </Text>
                <Text size="$xs" italic color="$textSecondary">
                  For {shortBrandName} users for a limited time.
                </Text>
              </YStack>
            </YStack>
            <View
              width={140}
              height={180}
              borderRadius="$150"
              overflow="hidden"
              position="absolute"
              top={-35}
              right={-15}
            >
              <Image
                source={Doctor}
                style={{ width: "100%", height: "100%" }}
              />
            </View>
          </XStack>
        </View>

        <YStack
          gap="$200"
          borderBottomLeftRadius="$150"
          borderBottomRightRadius="$150"
          borderWidth="$strokeWidth"
          borderColor="$shadowColor"
          borderTopWidth={0}
          padding="$250"
        >
          <XStack gap="$200" alignItems="center">
            <Smartphone size={24} color="$textSecondary" />
            <Text size="$sm">No need to download any apps</Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <Ban size={24} color="$textSecondary" />
            <Text size="$sm">No commitment or credit card needed</Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <Stethoscope size={24} color="$textSecondary" />
            <Text size="$sm">9+ million clinic messages sent</Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <DigitalHealth color={iconColor} />
            <Text size="$sm">800,000+ clinic visits since 2020</Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <Pills color={iconColor} />
            <Text size="$sm">Over 320,000 prescriptions completed</Text>
          </XStack>
        </YStack>

        <Text
          size="$xs"
          color="$textSecondary"
          fontStyle="italic"
          marginVertical="$200"
          textAlign="center"
        >
          {`Treatment is provided by ${medicalGroupName}`}
        </Text>
      </ScrollView>
      {stickyFooter}
    </>
  );
}
